import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Stack, Title, Text, Button, Paper, Group, Image, Box, Alert, Loader, Anchor,
} from '@mantine/core';
import { api } from '../../wheel-api/client';
import { UI } from '../../theme/kioskTheme';
import { LogosEvento } from './LogosEvento';
import { MARCAS, STAND } from '../../utils/cvdl';
import './cvdlClaro.css';

// Página pública de gemelos §5.2. Sin login, vertical y a una mano: la abre la
// persona con sus propios datos móviles mientras camina por el congreso.
export function GemeloPage() {
  const [params, setParams] = useSearchParams();
  const [numero, setNumero] = useState(params.get('n') || '');
  const [par, setPar] = useState(null);
  const [soyYo, setSoyYo] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);

  const buscar = useCallback(async (n) => {
    setCargando(true); setError(null); setPar(null); setSoyYo(null);
    try {
      setPar(await api.gemelosConsultar(Number(n)));
      setParams({ n }, { replace: true });
    } catch (err) {
      setError(err.data?.error === 'NUMERO_INVALIDO'
        ? 'Ese número no existe. Mirá tu caravana de nuevo.'
        : 'No pudimos consultar ahora. Probá en un minuto.');
    } finally { setCargando(false); }
  }, [setParams]);

  // El número inicial se congela al montar: si dependiéramos de `params`, cada
  // setParams dispararía otra consulta y entraríamos en bucle.
  const [inicial] = useState(() => params.get('n'));
  useEffect(() => { if (inicial) buscar(inicial); }, [inicial, buscar]);

  const reiniciar = () => { setPar(null); setSoyYo(null); setNumero(''); setParams({}, { replace: true }); };

  return (
    <Box className="cvdl-claro" style={{ minHeight: '100dvh', background: '#FFFFFF' }}>
      <Stack maw={480} mx="auto" px="lg" py="xl" gap="lg">
        {/* Esta es la pantalla del evento que más se ve fuera del stand: la
            abre la persona en su celular desde el link del WhatsApp. Las dos
            marcas van acá igual que en el kiosco. */}
        <LogosEvento h={34} c="var(--mantine-color-forvet-8)" />

        <Stack align="center" gap={2}>
          <Title order={1} ta="center" c="forvet.7" style={{ fontSize: 26 }}>TU CARAVANA</Title>
          <Text fz='var(--fs-small)' ta="center" c="forvet.6">Poné el número que dice tu caravana</Text>
        </Stack>

        <Group gap="sm" justify="center">
          <input
            value={numero}
            onChange={e => setNumero(e.target.value.replace(/\D/g, '').slice(0, 3))}
            inputMode="numeric"
            placeholder="137"
            style={{
              width: 130, height: 64, fontSize: 34, fontWeight: 800, textAlign: 'center',
              borderRadius: 12, border: `2px solid ${UI.borde}`, color: UI.tinta,
              fontFamily: 'Montserrat, sans-serif', letterSpacing: 3,
            }}
          />
          <Button h={64} disabled={!numero} loading={cargando} onClick={() => buscar(numero)}>
            BUSCAR
          </Button>
        </Group>

        {error && <Alert color="red" radius="md">{error}</Alert>}
        {cargando && <Loader mx="auto" />}

        {par?.estado === 'SIN_ENTREGAR' && (
          <Estado titulo="Todavía no está en juego">
            Ese número no se entregó todavía. Pasá por el stand {STAND} y buscá el tuyo.
          </Estado>
        )}

        {par?.estado === 'ABIERTO' && (
          <Estado titulo="Tu gemelo todavía no apareció">
            La otra mitad de tu número no se entregó. Volvé a mirar más tarde:
            se reparten caravanas toda la mañana y toda la tarde.
          </Estado>
        )}

        {(par?.estado === 'CERRADO' || par?.estado === 'ENCONTRADOS') && !soyYo && (
          <Stack gap="sm">
            <Title order={2} ta="center" c="forvet.7" style={{ fontSize: 20 }}>
              ¿CUÁL SOS VOS?
            </Title>
            <Group grow>
              {par.mitades.map(m => (
                <Button key={m.half} h={72} variant="outline"
                        onClick={() => setSoyYo(m.half)}>
                  {m.pistas.nombre || `Mitad ${m.half}`}
                </Button>
              ))}
            </Group>
          </Stack>
        )}

        {soyYo && <Gemelo par={par} yo={soyYo} onVolver={reiniciar} />}
      </Stack>
    </Box>
  );
}

function Estado({ titulo, children }) {
  return (
    <Paper withBorder radius="lg" p="lg" style={{ borderColor: UI.borde, borderWidth: 2 }}>
      <Text fw={800} size="lg" c="forvet.7" mb={4}>{titulo}</Text>
      <Text c="forvet.6">{children}</Text>
    </Paper>
  );
}

function Gemelo({ par, yo, onVolver }) {
  const otra = par.mitades.find(m => m.half !== yo);
  const p = otra?.pistas || {};

  const cuando = f => new Date(f).toLocaleString('es-AR',
    { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' });

  return (
    <Stack gap="md">
      <Title order={2} ta="center" c="forvet.7" style={{ fontSize: 22 }}>
        {par.estado === 'ENCONTRADOS' ? '¡YA SE ENCONTRARON!' : 'TU GEMELO YA APARECIÓ'}
      </Title>

      <Paper withBorder radius="lg" p="lg" style={{ borderColor: UI.accion, borderWidth: 2 }}>
        <Stack gap="sm" align="center">
          {p.fotoUrl && (
            <Box style={{ width: '100%', maxWidth: 260, borderRadius: 12, overflow: 'hidden' }}>
              <Image src={p.fotoUrl} alt="Tu gemelo" w="100%" fit="cover" />
            </Box>
          )}

          {p.nombre && (
            <Text fw={800} style={{ fontSize: 24 }} c="forvet.8">Se llama {p.nombre}</Text>
          )}

          {p.areas?.length > 0 && (
            <Text ta="center" c="forvet.6" size="lg">Le interesa {p.areas.join(' y ')}</Text>
          )}

          {p.dias?.length > 0 && (
            <Text fz='var(--fs-small)' ta="center" c="forvet.6">
              Anda por el congreso {p.dias.join(' y ')}
            </Text>
          )}

          {p.ciudad && <Text ta="center" c="forvet.6">Es de {p.ciudad}</Text>}
          {p.ubicacion && <Text ta="center" c="forvet.6">{p.ubicacion}</Text>}
        </Stack>
      </Paper>

      {/* La última pista ya no describe al gemelo: lo cita. Va destacada y
          arriba del resto porque es lo único accionable de la página. */}
      {p.cita && par.estado !== 'ENCONTRADOS' && (
        <Paper radius="lg" p="lg" withBorder
               style={{ borderColor: UI.accion, borderWidth: 3 }}>
          <Stack gap={4} align="center">
            <Text c="forvet.6" fw={600} tt="uppercase" fz="sm">Su cita</Text>
            <Text fw={800} c="forvet.8" ta="center" style={{ fontSize: 22 }}>
              {p.cita.diaNombre} a las {p.cita.hora}
            </Text>
            <Text ta="center" c="forvet.6">
              en el stand {STAND} de {MARCAS}
            </Text>
            <Text ta="center" c="forvet.6" fz="sm" mt={4}>
              Los dos reciben la misma hora. Vayan juntos al mostrador.
            </Text>
          </Stack>
        </Paper>
      )}

      {p.proxima && (
        <Alert color="blue" radius="md">
      <span style={{color:"var(--color-grand-background)"}}>
            {p.proxima.que === 'FOTO' ? 'La foto se libera'
            : p.proxima.que === 'CITA' ? 'La hora y el lugar del encuentro se liberan'
              : 'La próxima pista se libera'}
          {' '}el {cuando(p.proxima.at)}
      </span>
        </Alert>
      )}

      {par.estado !== 'ENCONTRADOS' && (
        <Paper radius="lg" p="lg" style={{ background: UI.accion }}>
          <Text c="#fff" fw={700} size="lg" ta="center">
            Cuando la encuentres, vengan los dos juntos al stand {STAND} de {MARCAS}.
            Ganan los dos.
          </Text>
        </Paper>
      )}

      <Anchor component="button" type="button" onClick={onVolver} ta="center" c="forvet.6">
        Consultar otro número
      </Anchor>
    </Stack>
  );
}
